import { Box, Grid2 as Grid, Stack, Typography } from "@mui/material";
import { type IconType } from "react-icons";
import { styles } from "./style";

export type DetailItemIcon =
  | { icon: IconType | null }
  | {
      customIconSrc: string;
    };

export type DetailItemType = DetailItemIcon & {
  caption?: string;
};

type DetailItemProps = {
  item: DetailItemType | null;
  textPosition?: "bottom" | "right";
};

const DetailItem = ({ item, textPosition }: DetailItemProps) => {
  if (!item) return null;
  const { caption } = item;

  let itemIcon: JSX.Element | null = null;
  if ("customIconSrc" in item) {
    const { customIconSrc } = item;
    itemIcon = (
      <Box
        component="img"
        sx={styles.customIcons}
        src={customIconSrc}
        alt="Icon"
      />
    );
  } else if ("icon" in item) {
    const { icon: Icon } = item;
    itemIcon = Icon ? <Icon size={30} /> : null;
  }

  return (
    <Stack
      direction={textPosition === "bottom" ? "column" : "row"}
      alignItems="center"
      spacing={1.5}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          display: "flex",
          justifyContent: "center",
          flexShrink: 0, // Prevent icon from shrinking
        }}
      >
        {itemIcon}
      </Box>
      {caption && (
        <Typography
          variant="caption"
          sx={{
            flexGrow: 1,
            textWrap: "balance",
            textAlign: textPosition === "bottom" ? "center" : "left",
          }}
        >
          {caption}
        </Typography>
      )}
    </Stack>
  );
};

interface ControlsCardProps {
  headers: DetailItemIcon[];
  rows: Array<DetailItemType[]>;
}

const ControlsCard = ({ headers, rows }: ControlsCardProps) => {
  return (
    <Grid container spacing={3} p={4} columnSpacing={4}>
      {headers.map((header, columnIndex) => (
        <Grid size={columnIndex === 0 ? 2 : "grow"}>
          <DetailItem key={columnIndex} item={header} textPosition="bottom" />
        </Grid>
      ))}
      {rows.map((row, rowIndex) => (
        <Grid key={rowIndex} container size={12}>
          {row.map((item, columnIndex) => (
            <Grid size={columnIndex === 0 ? 2 : "grow"}>
              <DetailItem
                key={columnIndex}
                item={item}
                textPosition={columnIndex === 0 ? "bottom" : "right"}
              />
            </Grid>
          ))}
        </Grid>
      ))}
    </Grid>
  );
};

export default ControlsCard;
